import { HybridCloudService } from '@qdrant/qdrant-cloud-public-api/hybrid/v1';
import { createFileRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';
import { assertRpcPermissions } from '@/api/permissions';
import { safeAsync } from '@/helpers/errors/safeAsync';
import { enqueueSnackbar } from '@/hooks/use-qdrant-snackbar';
import { head } from '@/router/Head';
import { resolvePaymentMethodAvailable } from '@/router/resolvers/payment';

export const Route = createFileRoute('/_authenticated/_dashboard/accounts/$accountId/hybrid-cloud/create')({
  beforeLoad: async ({ context: { queryClient }, params: { accountId } }) => {
    // The page is a form for `CreateHybridCloudEnvironment`, so gate it on what that RPC itself requires:
    // a read-only user gets Access Denied, and gets it before the payment check below could send them to
    // billing. Permissions come from cache — `$accountId` awaits them in its own beforeLoad, which runs first.
    assertRpcPermissions([HybridCloudService.method.createHybridCloudEnvironment], { queryClient, accountId });

    // Soft gate: the permissions can be there while the payment method is not. Unlike the check above this
    // one is a request, so it can fail — an unanswered check counts as "no payment method" and goes to
    // billing too, since the form it guards would be refused by the backend anyway.
    const [error, paymentMethodAvailable] = await safeAsync(resolvePaymentMethodAvailable({ accountId, queryClient }));
    if (error || !paymentMethodAvailable) {
      enqueueSnackbar('Please add payment information before creating a new Hybrid Cloud Environment.', {
        variant: 'info',
      });
      throw redirect({
        params: { accountId },
        replace: true,
        to: '/accounts/$accountId/billing',
      });
    }
  },
  head,
  staticData: {
    title: ['Hybrid Cloud Environment', 'Create'],
  },
  component: lazyRouteComponent(() => import('./create.component'), 'HybridCloudCreateComponent'),
  // The payment check above is a request, so the form's skeleton stands in while it runs.
  pendingMs: 200,
  pendingComponent: lazyRouteComponent(() => import('./create.component'), 'HybridCloudCreatePendingComponent'),
});
